// Welcome Page builder + renderer — free-canvas edition.
//
// The welcome page is a fixed-size design surface with freely placed elements:
//
//   { version: 2,
//     canvas: { width, height, bg, bgImageUrl },
//     items:  [ { id, type, x, y, w, h, props } ] }
//
// The editor (openWelcomeEditor) lets an arbiter drop banners/headings/text/
// images/maps/buttons/boxes anywhere on that surface and drag/resize them, with
// grid snapping and alignment guides. renderWelcomePage() draws the same model
// read-only for spectators, scaling the whole surface down proportionally to
// whatever width it is shown in, so the published page always matches the
// design pixel for pixel.
//
// Pages authored with the previous block-list editor ({ blocks: [...] }) are
// migrated on read (normalizeWelcomePage) — blocks are stacked top-to-bottom at full
// width, columns are split into side-by-side items — so old pages keep working
// and become freely movable the first time they are opened.
//
// Images are uploaded to Firebase Storage (window.authApp.uploadWelcomeImage)
// and only their URL is stored, so the model stays tiny.
//
// Editing happens on a working copy (editorDoc); only "Save Page" commits it
// back through the onSave callback (which stages state.draftWelcomePage and a
// derived plain-text summary). Closing without saving discards in-progress edits.
//
// The page model itself — the v2 shape, the v1 migration, the summary, and the
// arithmetic behind placing/ordering/resizing/snapping items — lives in
// js/utils/welcome-page.js, which has no DOM in it and is tested under Node
// (tests/welcome-page.test.js). This file is what draws it.

import { state } from '../state.js';
import {
    PAGE_WIDTHS, MIN_HEIGHT, MAX_HEIGHT, MIN_ITEM_W, MIN_ITEM_H, GRID,
    num, clamp, typeMeta, emptyPage,
    normalizeWelcomePage, welcomePageIsEmpty, cloneWelcomePage, htmlToPlainText,
    alignToStyle, valignToFlex, cssUrlValue,
    grownCanvasHeight, canvasHeightFloor, placeNewItem, duplicateItemOnCanvas,
    removeItem, reorderItems, resizeBox, snapTargets, applySnap
} from '../utils/welcome-page.js';

// js/app.js and js/ui/tournament-form.js reach the model through this module,
// which is where it used to live; re-exported so they need not know it moved.
export { welcomePageToSummary, welcomePageIsEmpty, normalizeWelcomePage } from '../utils/welcome-page.js';

const SNAP_TOL = 6;       // alignment-guide snap distance, in design pixels

// ----- shared helpers -----

const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'A', 'BR', 'P', 'DIV', 'SPAN', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'H4', 'BLOCKQUOTE']);

// DOMPurify config mirroring the legacy sanitizer's semantics: same formatting
// tag whitelist, all attributes stripped except a safe href on links. Unknown
// tags are unwrapped (KEEP_CONTENT) so their text survives, matching the old
// behavior. Tag names are lowercased for DOMPurify.
const DOMPURIFY_CONFIG = {
    ALLOWED_TAGS: Array.from(ALLOWED_TAGS).map(t => t.toLowerCase()),
    ALLOWED_ATTR: ['href'],
    KEEP_CONTENT: true
};

let domPurifyHookInstalled = false;

// Enforce the same href scheme restriction (https:/mailto: only) and re-apply
// target/rel that the legacy sanitizer did. DOMPurify already blocks
// javascript:/data: URIs, but the legacy code was stricter, so keep that.
function installDomPurifyHook(dp) {
    if (domPurifyHookInstalled) return;
    domPurifyHookInstalled = true;
    dp.addHook('afterSanitizeAttributes', (node) => {
        if (node.nodeName === 'A') {
            const href = node.getAttribute('href') || '';
            if (/^(https?:|mailto:)/i.test(href)) {
                node.setAttribute('target', '_blank');
                node.setAttribute('rel', 'noopener noreferrer');
            } else {
                node.removeAttribute('href');
            }
        }
    });
}

// Sanitize arbiter-authored rich text before it is rendered to spectators
// (stored-XSS guard). Prefer DOMPurify (hardened against mutation-XSS /
// parser-differential attacks); fall back to the legacy hand-rolled sanitizer
// only if DOMPurify failed to load. Never returns unsanitized HTML.
function sanitizeHtml(html) {
    const dp = typeof window !== 'undefined' ? window.DOMPurify : undefined;
    if (dp && typeof dp.sanitize === 'function') {
        installDomPurifyHook(dp);
        return dp.sanitize(html || '', DOMPURIFY_CONFIG);
    }
    console.warn('DOMPurify unavailable; using legacy fallback sanitizer for welcome-page HTML.');
    return legacySanitizeHtml(html);
}

// Legacy DOMParser-based sanitizer, retained as a fallback only.
function legacySanitizeHtml(html) {
    const doc = new DOMParser().parseFromString('<div>' + (html || '') + '</div>', 'text/html');
    const root = doc.body.firstChild;

    function clean(node) {
        const children = Array.from(node.childNodes);
        for (const child of children) {
            if (child.nodeType === Node.TEXT_NODE) continue;
            if (child.nodeType !== Node.ELEMENT_NODE) { child.remove(); continue; }
            if (!ALLOWED_TAGS.has(child.tagName)) {
                // Unwrap unknown tags: keep their text content, drop the element.
                const text = doc.createTextNode(child.textContent || '');
                child.replaceWith(text);
                continue;
            }
            // Strip all attributes (a safe href is re-applied to links afterward,
            // using the data-href stashed before this pass).
            const keepHref = child.tagName === 'A' ? child.getAttribute('data-href') : null;
            const attrs = Array.from(child.attributes);
            for (const a of attrs) child.removeAttribute(a.name);
            if (keepHref !== null) child.setAttribute('data-href', keepHref);
            clean(child);
        }
    }
    // Capture hrefs before stripping, then re-apply.
    root.querySelectorAll('a').forEach(a => { a.setAttribute('data-href', a.getAttribute('href') || ''); });
    clean(root);
    root.querySelectorAll('a').forEach(a => {
        const href = a.getAttribute('data-href') || '';
        a.removeAttribute('data-href');
        if (/^(https?:|mailto:)/i.test(href)) {
            a.setAttribute('href', href);
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
        }
    });
    return root.innerHTML;
}

function applyCanvasBackground(el, canvas) {
    el.style.background = canvas.bg || '';
    if (canvas.bgImageUrl) {
        el.style.backgroundImage = `url("${cssUrlValue(canvas.bgImageUrl)}")`;
        el.style.backgroundSize = 'cover';
        el.style.backgroundPosition = 'center';
    }
}

// Apply a banner's background (image with dark overlay, or a flat color).
function applyBannerBackground(el, p) {
    if (p.bgImageUrl) {
        el.style.backgroundImage = `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url("${cssUrlValue(p.bgImageUrl)}")`;
        el.style.backgroundSize = 'cover';
        el.style.backgroundPosition = 'center';
    } else {
        el.style.background = p.bgColor || '#1e293b';
    }
}

// Build a vertical "side banner" <img> for the given URL, or null if none.
function bannerSideImage(url) {
    if (!url) return null;
    const img = document.createElement('img');
    img.className = 'wp-banner-side';
    img.src = url;
    img.alt = '';
    img.loading = 'lazy';
    return img;
}

// ============================================================================
//  CONTENT (shared by the spectator render and the editor, so what the arbiter
//  drags around is exactly what gets published)
// ============================================================================

// `edit` = true builds the same visuals with contenteditable text and upload
// placeholders; false builds the read-only spectator version (returning null for
// items with nothing to show, so they are skipped entirely).
function buildContent(item, edit) {
    const p = item.props || {};
    switch (item.type) {
        case 'banner': {
            const hasSides = !!(p.leftImageUrl || p.rightImageUrl);
            const el = document.createElement('div');
            el.className = 'wp-banner' + (hasSides ? ' wp-banner-has-sides' : '');
            el.style.borderRadius = clamp(num(p.radius, 16), 0, 64) + 'px';
            applyBannerBackground(el, p);

            const content = document.createElement('div');
            content.className = 'wp-banner-content';
            content.style.textAlign = alignToStyle(p.align);
            content.style.color = p.textColor || '#fff';
            if (edit) {
                content.appendChild(editable(p.heading, 'wp-banner-heading', v => p.heading = v, tr('Banner heading')));
                content.appendChild(editable(p.subheading, 'wp-banner-sub', v => p.subheading = v, tr('Subheading (optional)')));
            } else {
                if (p.heading) {
                    const h = document.createElement('h1');
                    h.className = 'wp-banner-heading';
                    h.textContent = p.heading;
                    content.appendChild(h);
                }
                if (p.subheading) {
                    const s = document.createElement('p');
                    s.className = 'wp-banner-sub';
                    s.textContent = p.subheading;
                    content.appendChild(s);
                }
            }

            const left = bannerSideImage(p.leftImageUrl);
            const right = bannerSideImage(p.rightImageUrl);
            if (left) el.appendChild(left);
            el.appendChild(content);
            if (right) el.appendChild(right);
            return el;
        }
        case 'heading': {
            const lvl = ['h1', 'h2', 'h3', 'h4'].includes(p.level) ? p.level : 'h2';
            if (edit) {
                const ed = editable(p.text, 'wp-heading wp-' + lvl, v => p.text = v, tr('Heading text'));
                ed.style.textAlign = alignToStyle(p.align);
                if (p.color) ed.style.color = p.color;
                return ed;
            }
            if (!p.text) return null;
            const el = document.createElement(lvl);
            el.className = 'wp-heading wp-' + lvl;
            el.textContent = p.text;
            el.style.textAlign = alignToStyle(p.align);
            if (p.color) el.style.color = p.color;
            return el;
        }
        case 'text': {
            const clean = sanitizeHtml(p.html || '');
            if (!edit && !htmlToPlainText(clean)) return null;
            const el = document.createElement('div');
            el.className = 'wp-text';
            el.style.textAlign = alignToStyle(p.align);
            el.style.fontSize = clamp(num(p.fontSize, 17), 10, 72) + 'px';
            if (p.color) el.style.color = p.color;
            el.innerHTML = clean;
            if (edit) {
                el.classList.add('wpb-editable', 'wpb-richtext');
                el.contentEditable = 'true';
                el.dataset.placeholder = tr('Write something about your tournament — schedule, prizes, contact info…');
                el.spellcheck = false;
                el.addEventListener('input', () => {
                    // Restore :empty (so the placeholder shows) when all content is cleared.
                    if (!el.textContent.trim() && !el.querySelector('img,hr,li')) el.innerHTML = '';
                    p.html = el.innerHTML;
                });
                el.addEventListener('blur', () => { p.html = sanitizeHtml(el.innerHTML); });
            }
            return el;
        }
        case 'image': {
            if (!p.url) {
                if (!edit) return null;
                const drop = document.createElement('div');
                drop.className = 'wpb-image-drop';
                drop.innerHTML = `<i class="ri-image-add-line"></i><span>${tr('Click to upload an image')}</span>`;
                return drop;
            }
            const img = document.createElement('img');
            img.className = 'wp-image' + (p.rounded ? ' wp-rounded' : '');
            img.src = p.url;
            img.alt = p.alt || '';
            img.loading = 'lazy';
            img.style.objectFit = p.fit === 'contain' ? 'contain' : 'cover';
            return img;
        }
        case 'maps': {
            if (!p.address) {
                if (!edit) return null;
                const hint = document.createElement('div');
                hint.className = 'wpb-maps-placeholder';
                hint.innerHTML = `<i class="ri-map-pin-2-line"></i><span>${tr('Enter an address in the panel to the right to show a map.')}</span>`;
                return hint;
            }
            const iframe = document.createElement('iframe');
            iframe.className = 'wp-map';
            iframe.title = tr('Map:') + ' ' + p.address;
            iframe.src = `https://maps.google.com/maps?q=${encodeURIComponent(p.address)}&z=${clamp(num(p.zoom, 14), 1, 20)}&output=embed`;
            iframe.loading = 'lazy';
            iframe.setAttribute('allowfullscreen', '');
            iframe.referrerPolicy = 'no-referrer-when-downgrade';
            return iframe;
        }
        case 'button': {
            const wrap = document.createElement('div');
            wrap.className = 'wp-button-wrap';
            wrap.style.textAlign = alignToStyle(p.align);
            if (edit) {
                const btn = editable(p.label, 'wp-button', v => p.label = v, tr('Button label'));
                btn.style.background = p.bgColor || '#3b82f6';
                wrap.appendChild(btn);
                return wrap;
            }
            if (p.action === 'register') {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'wp-button';
                btn.textContent = p.label || tr('Register for this tournament');
                if (p.bgColor) btn.style.background = p.bgColor;
                btn.addEventListener('click', () => {
                    const real = document.getElementById('btn-open-registration');
                    if (real) real.click();
                });
                wrap.appendChild(btn);
            } else {
                const a = document.createElement('a');
                a.className = 'wp-button';
                a.textContent = p.label || 'Open';
                if (p.bgColor) a.style.background = p.bgColor;
                const href = p.url || '#';
                a.href = /^(https?:|mailto:)/i.test(href) ? href : '#';
                a.target = '_blank';
                a.rel = 'noopener noreferrer';
                wrap.appendChild(a);
            }
            return wrap;
        }
        case 'divider': {
            const wrap = document.createElement('div');
            wrap.className = 'wp-divider-wrap';
            const hr = document.createElement('hr');
            hr.className = 'wp-divider';
            hr.style.borderTopWidth = clamp(num(p.thickness, 1), 1, 12) + 'px';
            if (p.color) hr.style.borderTopColor = p.color;
            wrap.appendChild(hr);
            return wrap;
        }
        case 'box': {
            const el = document.createElement('div');
            el.className = 'wp-box';
            el.style.background = p.bgColor || 'transparent';
            el.style.borderRadius = clamp(num(p.radius, 14), 0, 200) + 'px';
            if (p.borderColor) el.style.border = '1px solid ' + p.borderColor;
            return el;
        }
        default:
            return null;
    }
}

// A contenteditable element bound to a model setter (no re-render on input).
function editable(value, className, setter, placeholder) {
    const el = document.createElement('div');
    el.className = 'wpb-editable ' + (className || '');
    el.contentEditable = 'true';
    el.spellcheck = false;
    el.textContent = value || '';
    if (placeholder) el.dataset.placeholder = placeholder;
    el.addEventListener('input', () => {
        // Keep the node truly :empty (browsers leave a stray <br> after a full delete)
        // so the gray CSS placeholder shows again.
        if (!el.textContent) el.innerHTML = '';
        setter(el.textContent);
    });
    return el;
}

// Position/size an item wrapper and set the flex alignment its content uses.
// Types without a `valign` prop own their whole box and are left to the
// stylesheet's `align-items: stretch`.
function layoutItemBox(el, item) {
    const p = item.props || {};
    el.style.left = item.x + 'px';
    el.style.top = item.y + 'px';
    el.style.width = item.w + 'px';
    el.style.height = item.h + 'px';
    if (p.valign) el.style.alignItems = valignToFlex(p.valign);
}

// ============================================================================
//  SPECTATOR RENDER
// ============================================================================

// The published surface has a fixed design width; here it is scaled down to fit
// whatever column it lands in (never scaled up past 1:1, just centered).
const scaleWatchers = new WeakMap();

function detachAutoScale(container) {
    const prev = scaleWatchers.get(container);
    if (prev) { prev.disconnect(); scaleWatchers.delete(container); }
}

function attachAutoScale(container, fit, stage, canvas) {
    let lastWidth = -1;
    const apply = () => {
        const avail = fit.clientWidth;
        if (!avail || avail === lastWidth) return;
        lastWidth = avail;
        const s = Math.min(1, avail / canvas.width);
        stage.style.transform = `scale(${s})`;
        stage.style.left = s >= 1 ? Math.round((avail - canvas.width) / 2) + 'px' : '0px';
        fit.style.height = Math.ceil(canvas.height * s) + 'px';
    };
    apply();
    if (typeof ResizeObserver === 'function') {
        const ro = new ResizeObserver(apply);
        ro.observe(fit);
        scaleWatchers.set(container, ro);
    } else {
        window.addEventListener('resize', apply);
        scaleWatchers.set(container, { disconnect() { window.removeEventListener('resize', apply); } });
    }
}

export function renderWelcomePage(page, container) {
    if (!container) return;
    detachAutoScale(container);
    container.innerHTML = '';
    if (welcomePageIsEmpty(page)) return;
    const p = normalizeWelcomePage(page);
    if (!p.items.length) return;

    const fit = document.createElement('div');
    fit.className = 'wp-fit';
    const stage = document.createElement('div');
    stage.className = 'wp-stage';
    stage.style.width = p.canvas.width + 'px';
    stage.style.height = p.canvas.height + 'px';
    applyCanvasBackground(stage, p.canvas);

    p.items.forEach(item => {
        const content = buildContent(item, false);
        if (!content) return;
        const el = document.createElement('div');
        el.className = 'wp-item wp-item-' + item.type;
        layoutItemBox(el, item);
        el.appendChild(content);
        stage.appendChild(el);
    });

    fit.appendChild(stage);
    container.appendChild(fit);
    attachAutoScale(container, fit, stage, p.canvas);
}

// ============================================================================
//  EDITOR
// ============================================================================

let editorDoc = emptyPage();
let onSaveCb = null;
let selectedId = null;
let editingId = null;        // item currently in inline text-editing mode
let zoomMode = 'fit';        // 'fit' or a number (1 = 100%)
let zoom = 1;
let snapEnabled = true;
let previewMode = false;
let editorWired = false;
let dragSession = null;      // active drag/resize gesture

function el(id) { return document.getElementById(id); }

function getUploadId() {
    const at = state.activeTournament;
    if (at && at.id && !String(at.id).startsWith('draft_')) return String(at.id);
    return 'draft';
}

export function openWelcomeEditor(onSave) {
    onSaveCb = typeof onSave === 'function' ? onSave : null;
    editorDoc = normalizeWelcomePage(state.draftWelcomePage);
    selectedId = null;
    editingId = null;
    previewMode = false;
    zoomMode = 'fit';
    wireEditorOnce();
    const modal = el('welcome-editor-modal');
    if (modal) modal.classList.add('active');
    syncChrome();
    renderStage();
}

function closeWelcomeEditor() {
    const modal = el('welcome-editor-modal');
    if (modal) modal.classList.remove('active');
    endDrag();
    if (itemToolbar) itemToolbar.style.display = 'none';
}

function saveWelcomeEditor() {
    commitPendingEdits();
    const page = cloneWelcomePage(editorDoc);
    if (onSaveCb) onSaveCb(page);
    closeWelcomeEditor();
}

// Inline contenteditable writes land on the model on `input`, but a rich-text
// field only re-sanitizes on blur — force that before reading the model out.
function commitPendingEdits() {
    const active = document.activeElement;
    if (active && active.isContentEditable) active.blur();
}

function wireEditorOnce() {
    if (editorWired) return;
    editorWired = true;

    const save = el('btn-welcome-editor-save');
    const cancel = el('btn-welcome-editor-cancel');
    const preview = el('btn-welcome-editor-preview');
    if (save) save.addEventListener('click', saveWelcomeEditor);
    if (cancel) cancel.addEventListener('click', closeWelcomeEditor);
    if (preview) preview.addEventListener('click', togglePreview);

    const palette = el('wpb-palette');
    if (palette) {
        palette.querySelectorAll('[data-block-type]').forEach(btn => {
            btn.addEventListener('click', () => addItem(btn.getAttribute('data-block-type')));
        });
    }

    const zoomSel = el('wpb-zoom');
    if (zoomSel) {
        zoomSel.addEventListener('change', () => {
            zoomMode = zoomSel.value === 'fit' ? 'fit' : Number(zoomSel.value);
            updateStageTransform();
        });
    }

    const snapBtn = el('wpb-snap');
    if (snapBtn) {
        snapBtn.addEventListener('click', () => {
            snapEnabled = !snapEnabled;
            syncChrome();
        });
    }

    // Clicking bare canvas deselects; the stage itself handles item gestures.
    const viewport = el('wpb-viewport');
    if (viewport) {
        viewport.addEventListener('pointerdown', (e) => {
            if (previewMode) return;
            if (e.target.closest('.wpb-item')) return;
            select(null);
        });
    }

    const heightHandle = el('wpb-height-handle');
    if (heightHandle) heightHandle.addEventListener('pointerdown', beginHeightDrag);

    window.addEventListener('resize', () => {
        const modal = el('welcome-editor-modal');
        if (modal && modal.classList.contains('active')) updateStageTransform();
    });

    document.addEventListener('keydown', onEditorKeyDown);
}

// ----- chrome -----

function syncChrome() {
    const btn = el('btn-welcome-editor-preview');
    if (btn) btn.innerHTML = previewMode
        ? `<i class="ri-edit-line"></i> ${tr('Edit')}`
        : `<i class="ri-eye-line"></i> ${tr('Preview')}`;
    const palette = el('wpb-palette');
    if (palette) palette.style.display = previewMode ? 'none' : '';
    const inspector = el('wpb-inspector');
    if (inspector) inspector.style.display = previewMode ? 'none' : '';
    const tools = el('wpb-canvas-tools');
    if (tools) tools.style.display = previewMode ? 'none' : '';
    const snapBtn = el('wpb-snap');
    if (snapBtn) {
        snapBtn.classList.toggle('wpb-tool-on', snapEnabled);
        snapBtn.title = snapEnabled ? tr('Snapping on') : tr('Snapping off');
    }
}

function togglePreview() {
    commitPendingEdits();
    previewMode = !previewMode;
    selectedId = null;
    editingId = null;
    syncChrome();
    renderStage();
}

// ----- stage -----

function renderStage() {
    const stage = el('wpb-stage');
    if (!stage) return;
    stage.innerHTML = '';
    stage.classList.toggle('wpb-stage-preview', previewMode);
    stage.style.width = editorDoc.canvas.width + 'px';
    stage.style.height = editorDoc.canvas.height + 'px';
    stage.style.background = '';
    stage.style.backgroundImage = '';
    applyCanvasBackground(stage, editorDoc.canvas);

    if (previewMode) {
        const holder = document.createElement('div');
        holder.className = 'wpb-preview-holder';
        editorDoc.items.forEach(item => {
            const content = buildContent(item, false);
            if (!content) return;
            const box = document.createElement('div');
            box.className = 'wp-item wp-item-' + item.type;
            layoutItemBox(box, item);
            box.appendChild(content);
            holder.appendChild(box);
        });
        stage.appendChild(holder);
    } else {
        editorDoc.items.forEach(item => stage.appendChild(buildEditorItem(item)));
        const guides = document.createElement('div');
        guides.className = 'wpb-guides';
        guides.id = 'wpb-guides';
        stage.appendChild(guides);
        if (!editorDoc.items.length) {
            const empty = document.createElement('div');
            empty.className = 'wpb-empty';
            empty.innerHTML = `<i class="ri-drag-move-2-line"></i><p>${tr('Your welcome page is empty.')}<br>${tr('Pick an element on the left, then drag it wherever you want.')}</p>`;
            stage.appendChild(empty);
        }
    }

    updateStageTransform();
    renderInspector();
}

function updateStageTransform() {
    const stage = el('wpb-stage');
    const wrap = el('wpb-stage-wrap');
    const viewport = el('wpb-viewport');
    if (!stage || !wrap || !viewport) return;
    const c = editorDoc.canvas;
    if (zoomMode === 'fit') {
        const avail = Math.max(120, viewport.clientWidth - 48);
        zoom = clamp(Math.min(1, avail / c.width), 0.2, 1);
    } else {
        zoom = clamp(Number(zoomMode) || 1, 0.2, 2);
    }
    stage.style.transform = `scale(${zoom})`;
    // Selection outline and resize handles divide the zoom back out, so they
    // stay the same size on screen instead of shrinking with the page.
    stage.style.setProperty('--wpb-ui', String(1 / zoom));
    wrap.style.width = Math.round(c.width * zoom) + 'px';
    wrap.style.height = Math.round(c.height * zoom) + 'px';
    positionToolbar();
}

function itemEl(id) {
    const stage = el('wpb-stage');
    return stage ? stage.querySelector(`.wpb-item[data-id="${id}"]`) : null;
}

function findItem(id) {
    return editorDoc.items.find(i => i.id === id) || null;
}

function buildEditorItem(item) {
    const box = document.createElement('div');
    box.className = 'wpb-item wpb-item-' + item.type + ' wp-item wp-item-' + item.type;
    box.dataset.id = item.id;
    layoutItemBox(box, item);
    if (item.id === selectedId) box.classList.add('wpb-selected');

    const content = buildContent(item, true);
    if (content) box.appendChild(content);

    // Resize handles (hidden by CSS unless the item is selected, so selection is
    // a pure class toggle and never rebuilds the DOM mid-gesture).
    ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].forEach(dir => {
        const h = document.createElement('div');
        h.className = 'wpb-handle wpb-handle-' + dir;
        h.dataset.dir = dir;
        box.appendChild(h);
    });

    box.addEventListener('pointerdown', (e) => onItemPointerDown(e, item));
    box.addEventListener('dblclick', (e) => onItemDoubleClick(e, item));
    return box;
}

// The action toolbar for the selected element is a single node living OUTSIDE
// the stage (in .wpb-stage-wrap): inside it, the stage's own clipping hid it for
// anything near the top edge, and the stage's zoom shrank its buttons.
let itemToolbar = null;

function ensureToolbar() {
    if (itemToolbar && itemToolbar.isConnected) return itemToolbar;
    const wrap = el('wpb-stage-wrap');
    if (!wrap) return null;
    const bar = document.createElement('div');
    bar.className = 'wpb-item-toolbar';
    const add = (icon, title, fn, extra) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'wpb-item-tool' + (extra ? ' ' + extra : '');
        b.title = title;
        b.innerHTML = `<i class="${icon}"></i>`;
        b.addEventListener('pointerdown', (e) => e.stopPropagation());
        b.addEventListener('click', (e) => {
            e.stopPropagation();
            const item = findItem(selectedId);
            if (item) fn(item);
        });
        bar.appendChild(b);
    };
    add('ri-file-copy-line', tr('Duplicate'), duplicateItem);
    add('ri-bring-to-front', tr('Bring to front'), (item) => reorderItem(item, 'front'));
    add('ri-send-to-back', tr('Send to back'), (item) => reorderItem(item, 'back'));
    add('ri-delete-bin-line', tr('Delete'), deleteItem, 'wpb-danger');
    wrap.appendChild(bar);
    itemToolbar = bar;
    return bar;
}

// Park the toolbar just above the selected element — or just below it when the
// element is at the very top of the page, so it is always on screen.
function positionToolbar() {
    const bar = ensureToolbar();
    if (!bar) return;
    const item = findItem(selectedId);
    if (!item || previewMode || dragSession) {
        bar.style.display = 'none';
        return;
    }
    bar.style.display = 'flex';
    const barW = bar.offsetWidth || 132;
    const barH = bar.offsetHeight || 34;
    const pageW = editorDoc.canvas.width * zoom;
    const pageH = editorDoc.canvas.height * zoom;
    let top = item.y * zoom - barH - 6;
    if (top < 0) top = Math.min((item.y + item.h) * zoom + 6, pageH - barH);
    bar.style.left = Math.round(clamp((item.x + item.w) * zoom - barW, 0, Math.max(0, pageW - barW))) + 'px';
    bar.style.top = Math.round(Math.max(0, top)) + 'px';
}

// ----- selection / item ops -----

function select(id) {
    if (selectedId === id) return;
    if (editingId && editingId !== id) exitEditing();
    selectedId = id;
    const stage = el('wpb-stage');
    if (stage) {
        stage.querySelectorAll('.wpb-item').forEach(node => {
            node.classList.toggle('wpb-selected', node.dataset.id === id);
        });
    }
    positionToolbar();
    renderInspector();
}

// Drop a new element into the first clear spot below what is already there.
function addItem(type) {
    const item = placeNewItem(editorDoc.canvas, editorDoc.items, type);
    if (!item) return;
    editorDoc.items.push(item);
    growCanvasFor(item);
    selectedId = item.id;
    renderStage();
    const node = itemEl(item.id);
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}

function duplicateItem(item) {
    const copy = duplicateItemOnCanvas(editorDoc.canvas, item);
    editorDoc.items.push(copy);
    growCanvasFor(copy);
    selectedId = copy.id;
    renderStage();
}

function deleteItem(item) {
    const next = removeItem(editorDoc.items, item.id);
    if (next === editorDoc.items) return;
    editorDoc.items = next;
    if (selectedId === item.id) selectedId = null;
    if (editingId === item.id) editingId = null;
    renderStage();
}

// Paint order == array order, so front/back is a move within the list.
function reorderItem(item, where) {
    const next = reorderItems(editorDoc.items, item.id, where);
    if (next === editorDoc.items) return;
    editorDoc.items = next;
    renderStage();
}

function growCanvasFor(item) {
    editorDoc.canvas.height = grownCanvasHeight(editorDoc.canvas.height, item);
}

// ----- inline text editing -----

function onItemDoubleClick(e, item) {
    if (previewMode) return;
    if (!['text', 'heading', 'banner', 'button'].includes(item.type)) {
        if (item.type === 'image') openImagePicker(url => { item.props.url = url; renderStage(); });
        return;
    }
    enterEditing(item, null, e);
}

// `target` (or the point under a double-click) decides which field gets the
// caret — a banner has both a heading and a subheading.
function enterEditing(item, target, event) {
    const node = itemEl(item.id);
    if (!node) return;
    editingId = item.id;
    node.classList.add('wpb-editing');
    if (!target && event) {
        // Editable text is pointer-inert until now, so re-resolve what was hit.
        const hit = document.elementFromPoint(event.clientX, event.clientY);
        if (hit && node.contains(hit)) target = hit.closest('.wpb-editable');
    }
    const field = target && node.contains(target) ? target : node.querySelector('.wpb-editable');
    if (field) {
        field.focus();
        const sel = window.getSelection();
        if (sel && field.childNodes.length) {
            const range = document.createRange();
            range.selectNodeContents(field);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }
}

function exitEditing() {
    if (!editingId) return;
    const node = itemEl(editingId);
    if (node) {
        node.classList.remove('wpb-editing');
        const focused = node.querySelector('.wpb-editable:focus');
        if (focused) focused.blur();
    }
    editingId = null;
}

// ----- drag / resize -----

function onItemPointerDown(e, item) {
    if (previewMode || e.button === 1 || e.button === 2) return;
    const handle = e.target.closest('.wpb-handle');
    // While an item is in text-edit mode, clicks inside its text must place the
    // caret rather than start a drag.
    if (!handle && editingId === item.id && e.target.closest('.wpb-editable')) {
        e.stopPropagation();
        return;
    }
    e.stopPropagation();
    e.preventDefault();
    if (editingId && editingId !== item.id) exitEditing();
    select(item.id);
    beginDrag(e, item, handle ? handle.dataset.dir : 'move');
}

function beginDrag(e, item, mode) {
    endDrag();
    const node = itemEl(item.id);
    if (!node) return;
    dragSession = {
        item, mode, node,
        startX: e.clientX,
        startY: e.clientY,
        orig: { x: item.x, y: item.y, w: item.w, h: item.h },
        targets: snapTargets(editorDoc.canvas, editorDoc.items, item.id),
        moved: false
    };
    node.classList.add('wpb-dragging');
    positionToolbar();          // hides it for the duration of the gesture
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', onDragEnd);
    window.addEventListener('pointercancel', onDragEnd);
}

function onDragMove(e) {
    const d = dragSession;
    if (!d) return;
    const dx = (e.clientX - d.startX) / zoom;
    const dy = (e.clientY - d.startY) / zoom;
    if (!d.moved && Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
    d.moved = true;

    const c = editorDoc.canvas;
    let box;
    if (d.mode === 'move') {
        box = { x: d.orig.x + dx, y: d.orig.y + dy, w: d.orig.w, h: d.orig.h };
    } else {
        box = resizeBox(d.orig, d.mode, dx, dy);
    }
    if (snapEnabled) {
        box.x = Math.round(box.x / GRID) * GRID;
        box.y = Math.round(box.y / GRID) * GRID;
        if (d.mode !== 'move') {
            box.w = Math.round(box.w / GRID) * GRID;
            box.h = Math.round(box.h / GRID) * GRID;
        }
    }
    const guides = snapEnabled ? applySnap(box, d.targets, SNAP_TOL / Math.max(zoom, 0.4), d.mode === 'move') : [];
    box.w = Math.max(MIN_ITEM_W, box.w);
    box.h = Math.max(MIN_ITEM_H, box.h);
    // Keep at least a sliver of the element reachable inside the surface.
    box.x = clamp(box.x, -box.w + 40, c.width - 40);
    box.y = Math.max(0, box.y);

    Object.assign(d.item, { x: Math.round(box.x), y: Math.round(box.y), w: Math.round(box.w), h: Math.round(box.h) });
    layoutItemBox(d.node, d.item);
    drawGuides(guides);
}

function onDragEnd() {
    const d = dragSession;
    endDrag();
    if (!d) return;
    if (d.moved) {
        growCanvasFor(d.item);
        updateStageTransform();
        const stage = el('wpb-stage');
        if (stage) stage.style.height = editorDoc.canvas.height + 'px';
        renderInspector();
    }
    positionToolbar();
}

function endDrag() {
    if (dragSession && dragSession.node) dragSession.node.classList.remove('wpb-dragging');
    dragSession = null;
    drawGuides([]);
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', onDragEnd);
    window.removeEventListener('pointercancel', onDragEnd);
}

function drawGuides(guides) {
    const layer = el('wpb-guides');
    if (!layer) return;
    layer.innerHTML = '';
    (guides || []).forEach(g => {
        const line = document.createElement('div');
        line.className = 'wpb-guide wpb-guide-' + g.axis;
        if (g.axis === 'v') line.style.left = g.at + 'px';
        else line.style.top = g.at + 'px';
        layer.appendChild(line);
    });
}

// Dragging the bar under the surface sets the published page height.
function beginHeightDrag(e) {
    if (previewMode) return;
    e.preventDefault();
    const startY = e.clientY;
    const startH = editorDoc.canvas.height;
    const floor = canvasHeightFloor(editorDoc.items);
    const onMove = (ev) => {
        const next = clamp(Math.round((startH + (ev.clientY - startY) / zoom) / GRID) * GRID, Math.max(MIN_HEIGHT, floor), MAX_HEIGHT);
        editorDoc.canvas.height = next;
        const stage = el('wpb-stage');
        if (stage) stage.style.height = next + 'px';
        updateStageTransform();
    };
    const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        renderInspector();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
}

// ----- keyboard -----

function onEditorKeyDown(e) {
    const modal = el('welcome-editor-modal');
    if (!modal || !modal.classList.contains('active') || previewMode) return;
    if (e.key === 'Escape') {
        if (editingId) { exitEditing(); e.preventDefault(); return; }
        if (selectedId) { select(null); e.preventDefault(); return; }
        return;
    }
    if (editingId) return;
    const active = document.activeElement;
    if (active && (active.isContentEditable || ['INPUT', 'SELECT', 'TEXTAREA'].includes(active.tagName))) return;
    const item = findItem(selectedId);
    if (!item) return;

    if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteItem(item);
        return;
    }
    if ((e.ctrlKey || e.metaKey) && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault();
        duplicateItem(item);
        return;
    }
    const step = e.shiftKey ? GRID : 1;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const m = moves[e.key];
    if (!m) return;
    e.preventDefault();
    item.x = clamp(item.x + m[0], -item.w + 40, editorDoc.canvas.width - 40);
    item.y = Math.max(0, item.y + m[1]);
    const node = itemEl(item.id);
    if (node) layoutItemBox(node, item);
    growCanvasFor(item);
    const stage = el('wpb-stage');
    if (stage) stage.style.height = editorDoc.canvas.height + 'px';
    updateStageTransform();
    renderInspector();
}

// ============================================================================
//  INSPECTOR (right-hand properties panel)
// ============================================================================

function renderInspector() {
    const panel = el('wpb-inspector');
    if (!panel) return;
    panel.innerHTML = '';
    const item = findItem(selectedId);
    panel.appendChild(item ? buildItemInspector(item) : buildPageInspector());
}

function inspectorSection(title, icon) {
    const sec = document.createElement('div');
    sec.className = 'wpb-insp-section';
    const h = document.createElement('h4');
    h.innerHTML = `<i class="${icon}"></i> ${title}`;
    sec.appendChild(h);
    return sec;
}

function addRow(parent, labelText, control) {
    const row = document.createElement('label');
    row.className = 'wpb-set-row';
    const span = document.createElement('span');
    span.textContent = tr(labelText);
    row.appendChild(span);
    row.appendChild(control);
    parent.appendChild(row);
    return row;
}

function selectCtl(value, options, onChange) {
    const s = document.createElement('select');
    options.forEach(o => {
        const opt = document.createElement('option');
        opt.value = o.value;
        opt.textContent = tr(o.label);
        if (String(o.value) === String(value)) opt.selected = true;
        s.appendChild(opt);
    });
    s.addEventListener('change', () => onChange(s.value));
    return s;
}

function colorCtl(value, onChange, allowClear) {
    const box = document.createElement('span');
    box.className = 'wpb-color-ctl';
    const c = document.createElement('input');
    c.type = 'color';
    c.value = value || '#000000';
    c.addEventListener('input', () => onChange(c.value));
    box.appendChild(c);
    if (allowClear) {
        const clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'wpb-mini-btn';
        clear.title = tr('Clear');
        clear.innerHTML = '<i class="ri-close-line"></i>';
        clear.addEventListener('click', () => onChange(''));
        box.appendChild(clear);
    }
    return box;
}

function numberCtl(value, onChange, opts = {}) {
    const n = document.createElement('input');
    n.type = 'number';
    n.className = 'wpb-num';
    if (opts.min != null) n.min = String(opts.min);
    if (opts.max != null) n.max = String(opts.max);
    n.step = String(opts.step || 1);
    n.value = String(Math.round(num(value, 0)));
    n.addEventListener('change', () => onChange(clamp(num(n.value, 0), opts.min != null ? opts.min : -Infinity, opts.max != null ? opts.max : Infinity)));
    return n;
}

function rangeCtl(value, onChange, opts = {}) {
    const r = document.createElement('input');
    r.type = 'range';
    r.min = String(opts.min != null ? opts.min : 0);
    r.max = String(opts.max != null ? opts.max : 100);
    r.step = String(opts.step || 1);
    r.value = String(num(value, opts.min || 0));
    r.addEventListener('input', () => onChange(Number(r.value)));
    return r;
}

function textCtl(value, onChange, placeholder, type) {
    const i = document.createElement('input');
    i.type = type || 'text';
    i.value = value || '';
    if (placeholder) i.placeholder = placeholder;
    i.addEventListener('change', () => onChange(i.value));
    return i;
}

function checkCtl(checked, onChange) {
    const c = document.createElement('input');
    c.type = 'checkbox';
    c.checked = !!checked;
    c.addEventListener('change', () => onChange(c.checked));
    return c;
}

const alignOptions = [{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }];
const valignOptions = [{ value: 'top', label: 'Top' }, { value: 'middle', label: 'Middle' }, { value: 'bottom', label: 'Bottom' }];

// Re-render just the selected item in place (settings that change its look).
function refreshItem(item) {
    const node = itemEl(item.id);
    if (!node) { renderStage(); return; }
    const fresh = buildEditorItem(item);
    node.replaceWith(fresh);
}

function buildPageInspector() {
    const frag = document.createDocumentFragment();
    const sec = inspectorSection(tr('Page'), 'ri-artboard-line');
    const c = editorDoc.canvas;

    addRow(sec, 'Page width', selectCtl(c.width, PAGE_WIDTHS.map(w => ({ value: w, label: w + ' px' })), v => {
        c.width = Number(v);
        editorDoc.items.forEach(i => { i.x = Math.min(i.x, c.width - 40); });
        renderStage();
    }));
    addRow(sec, 'Page height', numberCtl(c.height, v => {
        const floor = canvasHeightFloor(editorDoc.items);
        c.height = clamp(Math.round(v), Math.max(MIN_HEIGHT, floor), MAX_HEIGHT);
        renderStage();
    }, { min: MIN_HEIGHT, max: MAX_HEIGHT, step: 10 }));
    addRow(sec, 'Background color', colorCtl(c.bg || '#ffffff', v => { c.bg = v; renderStage(); }, true));
    addRow(sec, 'Background image', imagePropCtl(c, 'bgImageUrl', tr('Remove background image'), () => renderStage()));
    frag.appendChild(sec);

    const hint = document.createElement('p');
    hint.className = 'wpb-insp-hint';
    hint.innerHTML = `${tr('Select an element to edit it.')}<br>${tr('Double-click text to type, drag the corners to resize.')}`;
    frag.appendChild(hint);
    return frag;
}

function buildItemInspector(item) {
    const frag = document.createDocumentFragment();
    const p = item.props;
    const meta = typeMeta(item.type);

    // Position & size.
    const geo = inspectorSection(tr(meta ? meta.label : item.type), meta ? meta.icon : 'ri-square-line');
    const grid = document.createElement('div');
    grid.className = 'wpb-geo-grid';
    const geoField = (label, value, onChange) => {
        const wrap = document.createElement('label');
        wrap.className = 'wpb-geo-field';
        const s = document.createElement('span');
        s.textContent = label;
        wrap.appendChild(s);
        wrap.appendChild(numberCtl(value, onChange, { step: 1 }));
        grid.appendChild(wrap);
    };
    const applyGeo = () => {
        const node = itemEl(item.id);
        if (node) layoutItemBox(node, item);
        growCanvasFor(item);
        const stage = el('wpb-stage');
        if (stage) stage.style.height = editorDoc.canvas.height + 'px';
        updateStageTransform();
    };
    geoField('X', item.x, v => { item.x = Math.round(v); applyGeo(); });
    geoField('Y', item.y, v => { item.y = Math.max(0, Math.round(v)); applyGeo(); });
    geoField(tr('Width'), item.w, v => { item.w = Math.max(MIN_ITEM_W, Math.round(v)); applyGeo(); });
    geoField(tr('Height'), item.h, v => { item.h = Math.max(MIN_ITEM_H, Math.round(v)); applyGeo(); });
    geo.appendChild(grid);

    const quick = document.createElement('div');
    quick.className = 'wpb-quick-row';
    const quickBtn = (icon, title, fn) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'btn-outline wpb-mini-btn';
        b.title = title;
        b.innerHTML = `<i class="${icon}"></i>`;
        b.addEventListener('click', fn);
        quick.appendChild(b);
    };
    quickBtn('ri-align-item-horizontal-center', tr('Center horizontally'), () => {
        item.x = Math.round((editorDoc.canvas.width - item.w) / 2);
        applyGeo();
        renderInspector();
    });
    quickBtn('ri-expand-horizontal-line', tr('Full width'), () => {
        item.x = 0; item.w = editorDoc.canvas.width;
        applyGeo();
        renderInspector();
    });
    quickBtn('ri-file-copy-line', tr('Duplicate'), () => duplicateItem(item));
    quickBtn('ri-delete-bin-line', tr('Delete'), () => deleteItem(item));
    geo.appendChild(quick);
    frag.appendChild(geo);

    // Type-specific settings.
    const sec = inspectorSection(tr('Settings'), 'ri-settings-3-line');
    const redraw = () => refreshItem(item);

    switch (item.type) {
        case 'banner':
            addRow(sec, 'Background color', colorCtl(p.bgColor, v => { p.bgColor = v; redraw(); }));
            addRow(sec, 'Text color', colorCtl(p.textColor, v => { p.textColor = v; redraw(); }));
            addRow(sec, 'Alignment', selectCtl(p.align, alignOptions, v => { p.align = v; redraw(); }));
            addRow(sec, 'Corner radius', rangeCtl(p.radius, v => { p.radius = v; redraw(); }, { min: 0, max: 48 }));
            addRow(sec, 'Background image', imagePropCtl(p, 'bgImageUrl', tr('Remove background image'), redraw));
            addRow(sec, 'Left side banner', imagePropCtl(p, 'leftImageUrl', tr('Remove left side banner'), redraw));
            addRow(sec, 'Right side banner', imagePropCtl(p, 'rightImageUrl', tr('Remove right side banner'), redraw));
            break;
        case 'heading':
            addRow(sec, 'Level', selectCtl(p.level || 'h2', [
                { value: 'h1', label: 'H1 — Large' }, { value: 'h2', label: 'H2 — Medium' },
                { value: 'h3', label: 'H3 — Small' }, { value: 'h4', label: 'H4 — Tiny' }
            ], v => { p.level = v; redraw(); }));
            addRow(sec, 'Alignment', selectCtl(p.align, alignOptions, v => { p.align = v; redraw(); }));
            addRow(sec, 'Vertical', selectCtl(p.valign, valignOptions, v => { p.valign = v; redraw(); }));
            addRow(sec, 'Color', colorCtl(p.color || '#222222', v => { p.color = v; redraw(); }, true));
            break;
        case 'text':
            sec.appendChild(buildRichTextToolbar(item));
            addRow(sec, 'Font size', rangeCtl(p.fontSize, v => { p.fontSize = v; redraw(); }, { min: 11, max: 42 }));
            addRow(sec, 'Alignment', selectCtl(p.align, alignOptions, v => { p.align = v; redraw(); }));
            addRow(sec, 'Vertical', selectCtl(p.valign, valignOptions, v => { p.valign = v; redraw(); }));
            addRow(sec, 'Color', colorCtl(p.color || '#333333', v => { p.color = v; redraw(); }, true));
            break;
        case 'image':
            addRow(sec, 'Image', imagePropCtl(p, 'url', tr('Remove image'), redraw));
            addRow(sec, 'Fit', selectCtl(p.fit, [
                { value: 'cover', label: 'Fill the box (crop)' }, { value: 'contain', label: 'Fit inside the box' }
            ], v => { p.fit = v; redraw(); }));
            addRow(sec, 'Rounded corners', checkCtl(p.rounded !== false, v => { p.rounded = v; redraw(); }));
            addRow(sec, 'Alt text', textCtl(p.alt, v => { p.alt = v; }, tr('Alt text')));
            break;
        case 'maps':
            addRow(sec, 'Address', textCtl(p.address, v => { p.address = v.trim(); redraw(); }, tr('Address or place')));
            addRow(sec, 'Zoom', rangeCtl(p.zoom, v => { p.zoom = v; redraw(); }, { min: 1, max: 20 }));
            break;
        case 'button':
            addRow(sec, 'Action', selectCtl(p.action || 'register', [
                { value: 'register', label: 'Open registration' }, { value: 'url', label: 'Link to a URL' }
            ], v => { p.action = v; redraw(); renderInspector(); }));
            if (p.action === 'url') addRow(sec, 'URL', textCtl(p.url, v => { p.url = v; }, 'https://…', 'url'));
            addRow(sec, 'Color', colorCtl(p.bgColor || '#3b82f6', v => { p.bgColor = v; redraw(); }));
            addRow(sec, 'Alignment', selectCtl(p.align, alignOptions, v => { p.align = v; redraw(); }));
            addRow(sec, 'Vertical', selectCtl(p.valign, valignOptions, v => { p.valign = v; redraw(); }));
            break;
        case 'box':
            addRow(sec, 'Background color', colorCtl(p.bgColor || '#f1f5f9', v => { p.bgColor = v; redraw(); }, true));
            addRow(sec, 'Border color', colorCtl(p.borderColor || '#cbd5e1', v => { p.borderColor = v; redraw(); }, true));
            addRow(sec, 'Corner radius', rangeCtl(p.radius, v => { p.radius = v; redraw(); }, { min: 0, max: 80 }));
            break;
        case 'divider':
            addRow(sec, 'Color', colorCtl(p.color || '#cbd5e1', v => { p.color = v; redraw(); }, true));
            addRow(sec, 'Thickness', rangeCtl(p.thickness, v => { p.thickness = v; redraw(); }, { min: 1, max: 10 }));
            break;
    }
    if (sec.children.length > 1) frag.appendChild(sec);
    return frag;
}

// Rich-text formatting for the selected Text element. mousedown is suppressed so
// the caret/selection inside the contenteditable survives the button press.
function buildRichTextToolbar(item) {
    const toolbar = document.createElement('div');
    toolbar.className = 'wpb-rt-toolbar';
    const mk = (icon, cmd, title) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'wpb-rt-btn';
        b.title = title;
        b.innerHTML = `<i class="${icon}"></i>`;
        b.addEventListener('mousedown', (e) => e.preventDefault());
        b.addEventListener('click', () => {
            const node = itemEl(item.id);
            const area = node ? node.querySelector('.wpb-richtext') : null;
            if (!area) return;
            if (!node.classList.contains('wpb-editing')) enterEditing(item, area);
            if (document.activeElement !== area) area.focus();
            if (cmd === 'createLink') {
                const url = window.prompt(tr('Link URL (https://…):'), 'https://');
                if (url) document.execCommand('createLink', false, url);
            } else {
                document.execCommand(cmd, false, null);
            }
            item.props.html = area.innerHTML;
        });
        toolbar.appendChild(b);
    };
    mk('ri-bold', 'bold', tr('Bold'));
    mk('ri-italic', 'italic', tr('Italic'));
    mk('ri-underline', 'underline', tr('Underline'));
    mk('ri-list-unordered', 'insertUnorderedList', tr('Bulleted list'));
    mk('ri-list-ordered', 'insertOrderedList', tr('Numbered list'));
    mk('ri-link', 'createLink', tr('Insert link'));
    mk('ri-eraser-line', 'removeFormat', tr('Clear formatting'));
    return toolbar;
}

// A settings control that picks/clears an image stored on target[prop]. Used for
// element images, banner backgrounds/side banners and the page background.
function imagePropCtl(target, prop, removeTitle, after) {
    const box = document.createElement('div');
    box.className = 'wpb-bgimg-ctl';

    if (target[prop]) {
        const thumb = document.createElement('img');
        thumb.className = 'wpb-bgimg-thumb';
        thumb.src = target[prop];
        thumb.alt = '';
        box.appendChild(thumb);
    }

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn-outline wpb-mini-btn';
    btn.innerHTML = target[prop] ? `<i class="ri-image-edit-line"></i> ${tr('Replace')}` : `<i class="ri-upload-2-line"></i> ${tr('Choose')}`;
    btn.addEventListener('click', () => {
        openImagePicker(url => {
            target[prop] = url;
            if (after) after();
            renderInspector();
        });
    });
    box.appendChild(btn);

    if (target[prop]) {
        const clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'btn-outline wpb-mini-btn wpb-danger';
        clear.innerHTML = '<i class="ri-close-line"></i>';
        clear.title = removeTitle || tr('Remove image');
        clear.addEventListener('click', () => {
            target[prop] = '';
            if (after) after();
            renderInspector();
        });
        box.appendChild(clear);
    }
    return box;
}

// ----- reusable image picker (re-use previously uploaded images) -----
//
// A single overlay, built lazily and reused. onPick(url) is called with the
// chosen image URL (either a previously uploaded one, re-used without a second
// upload, or a freshly uploaded file). Cancelling calls nothing.

let imagePicker = null;      // { overlay, grid, onPick } once built

function buildImagePicker() {
    const overlay = document.createElement('div');
    overlay.className = 'wpb-img-picker-overlay';
    overlay.innerHTML = `
        <div class="wpb-img-picker" role="dialog" aria-modal="true">
            <div class="wpb-img-picker-head">
                <h3><i class="ri-image-line"></i> ${tr('Choose an image')}</h3>
                <button type="button" class="wpb-img-picker-close" aria-label="${tr('Close')}"><i class="ri-close-line"></i></button>
            </div>
            <div class="wpb-img-picker-grid"></div>
        </div>`;
    const close = () => hideImagePicker();
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    overlay.querySelector('.wpb-img-picker-close').addEventListener('click', close);
    document.body.appendChild(overlay);
    imagePicker = { overlay, grid: overlay.querySelector('.wpb-img-picker-grid'), onPick: null };
    return imagePicker;
}

function hideImagePicker() {
    if (imagePicker) imagePicker.overlay.classList.remove('active');
}

async function openImagePicker(onPick) {
    if (!window.authApp || typeof window.authApp.uploadWelcomeImage !== 'function') {
        window.Dialog.warn(tr('Image upload is unavailable — please sign in and try again.'), tr('Upload Unavailable'));
        return;
    }
    const pk = imagePicker || buildImagePicker();
    pk.onPick = onPick;
    pk.overlay.classList.add('active');
    renderImagePickerGrid();
    // Fetch the user's library, then re-render the grid with the thumbnails.
    let images = [];
    try {
        if (typeof window.authApp.listMyWelcomeImages === 'function') {
            images = await window.authApp.listMyWelcomeImages();
        }
    } catch (e) { console.warn('Could not load image library:', e); }
    // Ignore if the picker was closed/reopened while we were awaiting.
    if (pk.onPick === onPick && pk.overlay.classList.contains('active')) {
        renderImagePickerGrid(images);
    }
}

// Render the picker grid: an "upload new" tile first, then library thumbnails.
// Passing null (before the library resolves) shows a loading placeholder.
function renderImagePickerGrid(images) {
    const pk = imagePicker;
    if (!pk) return;
    pk.grid.innerHTML = '';

    const uploadTile = document.createElement('button');
    uploadTile.type = 'button';
    uploadTile.className = 'wpb-img-tile wpb-img-tile-upload';
    uploadTile.innerHTML = `<i class="ri-upload-2-line"></i><span>${tr('Upload new')}</span>`;
    uploadTile.addEventListener('click', uploadNewFromPicker);
    pk.grid.appendChild(uploadTile);

    if (images == null) {
        const loading = document.createElement('div');
        loading.className = 'wpb-img-picker-hint';
        loading.textContent = tr('Loading your images…');
        pk.grid.appendChild(loading);
        return;
    }
    if (images.length === 0) {
        const hint = document.createElement('div');
        hint.className = 'wpb-img-picker-hint';
        hint.textContent = tr('No images yet — upload one to reuse it later.');
        pk.grid.appendChild(hint);
        return;
    }
    images.forEach(item => {
        const tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'wpb-img-tile';
        tile.title = item.name || '';
        const img = document.createElement('img');
        img.src = item.url;
        img.alt = item.name || '';
        img.loading = 'lazy';
        tile.appendChild(img);
        tile.addEventListener('click', () => {
            const cb = pk.onPick;
            hideImagePicker();
            if (cb) cb(item.url);
        });
        pk.grid.appendChild(tile);
    });
}

// "Upload new" tile: open the OS file dialog, upload, then deliver the URL.
function uploadNewFromPicker() {
    const pk = imagePicker;
    if (!pk) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', async () => {
        const file = input.files && input.files[0];
        if (!file) return;
        const cb = pk.onPick;
        pk.grid.classList.add('wpb-img-picker-busy');
        try {
            const url = await window.authApp.uploadWelcomeImage(getUploadId(), file);
            hideImagePicker();
            if (cb) cb(url);
        } catch (err) {
            console.error('Image upload failed:', err);
            window.Dialog.error(tr('Image upload failed:') + ' ' + (err && err.message ? err.message : err), tr('Upload Failed'));
        } finally {
            pk.grid.classList.remove('wpb-img-picker-busy');
        }
    });
    input.click();
}
